import { usePlanningStore } from '@planning/AppProvider'
import { IContainerJourneyItem } from '@planning/pages/Issues/Stores/ContainerJourneyItem'
import { RailcarAssignmentDialog } from '@planning/pages/RailVisit/Components/Dialogs/RailcarAssignmentDialog'
import { useContainerYardOperationStore } from '@planning/pages/RailVisit/RailVisitDetailsPage'
import { OrderListMenuOptions } from '@planning/pages/VesselVisit/Components/molecules/OrderListMenuOptions'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { orderService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { ButtonOption } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'

type Props = {
  order: IOrderItem
  journey?: IContainerJourneyItem
}

export const RailcarPlanningOrderMenu: FC<Props> = observer(({ order, journey }) => {
  const { t } = useTranslate()
  const { appViewStore, railVisitItemStore } = usePlanningStore()

  const [modalState, setModalState] = useState<'assign' | 'move' | null>(null)

  const containerYardOperationViewStore = useContainerYardOperationStore()

  const handleAssignToRailcar = async (railcarId: number) => {
    try {
      appViewStore.showLoader()

      const visitId = order.data.carrierVisitId
      const visitItem = railVisitItemStore.getById(visitId!)

      const targetPosition = visitItem?.railcarTrackPositions.find(
        p => p.data.railcarId === railcarId,
      )

      if (!targetPosition) {
        throw new Error('Railcar position not found')
      }

      await orderService.assignToRailcarTrackPosition({
        orderId: order.id,
        railcarTrackPositionId: targetPosition.id,
      })

      appViewStore.setShowAlert('success', t('savedSuccessfully', 'Saved successfully'))
    } catch (e) {
      appViewStore.setShowAlert('error', t('failedToAssign', 'Failed to assign'))
    } finally {
      appViewStore.hideLoader()
    }
  }

  const handleUnassign = async () => {
    const confirmed = await appViewStore.setOpenConfirmDialog(
      true,
      t('confirmUnassign', 'Are you sure you want to unassign this container?'),
      t('unassign', 'Unassign'),
    )

    if (confirmed) {
      try {
        appViewStore.showLoader()
        await orderService.assignToRailcarTrackPosition({
          orderId: order.id,
          railcarTrackPositionId: null,
        })
        appViewStore.setShowAlert('success', t('unassignedSuccessfully', 'Unassigned successfully'))
      } catch (e) {
        appViewStore.setShowAlert('error', t('failedToUnassign', 'Failed to unassign'))
      } finally {
        appViewStore.hideLoader()
      }
    }
  }

  const railcarOptions: ButtonOption[] = []

  if (order.data.railcarId) {
    railcarOptions.push(
      {
        label: t('unassign', 'Unassign'),
        onClick: () => handleUnassign(),
      },
      {
        label: t('move', 'Move'),
        onClick: () => setModalState('move'),
      },
    )
  } else {
    railcarOptions.push({
      label: t('assignToRailcar', 'Assign to railcar'),
      onClick: () => setModalState('assign'),
    })
  }

  return (
    <>
      <OrderListMenuOptions
        order={order}
        journey={journey}
        extraOptions={railcarOptions}
        size='small'
        containerYardOperationViewStore={containerYardOperationViewStore!}
      />

      {modalState === 'assign' && (
        <RailcarAssignmentDialog
          open={true}
          onClose={() => setModalState(null)}
          onConfirm={handleAssignToRailcar}
          title={t('assignToRailcar', 'Assign to railcar')}
          confirmLabel={t('assign', 'Assign')}
          railVisitId={order.data.carrierVisitId!}
        />
      )}

      {modalState === 'move' && (
        <RailcarAssignmentDialog
          open={true}
          onClose={() => setModalState(null)}
          onConfirm={handleAssignToRailcar}
          title={t('moveToAnotherRailcar', 'Move to another railcar')}
          confirmLabel={t('move', 'Move')}
          railVisitId={order.data.carrierVisitId!}
          excludeCurrentRailcarId={order.data.railcarId!}
        />
      )}
    </>
  )
})
